import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import { Delete as DeleteIcon, Update as UpdateIcon, Add as AddIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { StockService } from '../services/stockService';

interface BulkImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (stocks: ImportedStock[]) => void;
}

export interface ImportedStock {
  symbol: string;
  companyName?: string;
  currentPrice?: number;
  pivotPrice?: number;
  stopLoss?: number;
  targetPrice?: number;
  marketCap?: number;
  indGroupRank?: number;
  epsRating?: number;
  rsRating?: number;
  isUpdate?: boolean;
  // New fields from expanded MarketSurge export
  weeklyChgToPivot?: number;
  weeklyBaseType?: string;
  weeklyBaseStage?: string;
  priceChgPercent?: number;
  percentOffHigh?: number;
  volume?: number;
  volumeChgVs50Day?: number;
  avgDailyVolume?: number; // 50-day average volume in thousands
  compRating?: number;
  adRating?: string;
  smrRating?: string;
  industryName?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const BulkImportDialog: React.FC<BulkImportDialogProps> = ({ open, onClose, onImport }) => {
  const [tabValue, setTabValue] = useState(0);
  const [importText, setImportText] = useState('');
  const [parsedStocks, setParsedStocks] = useState<ImportedStock[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);

  useEffect(() => {
    if (parsedStocks.length > 0 && !checking) {
      checkExistingStocks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedStocks.length]); // Only depend on length to avoid infinite loop

  const checkExistingStocks = async () => {
    setChecking(true);
    const updatedStocks = await Promise.all(
      parsedStocks.map(async (stock) => {
        const exists = await StockService.stockExists(stock.symbol);
        console.log(`Stock ${stock.symbol} exists: ${exists}`);
        return { ...stock, isUpdate: exists };
      })
    );
    setParsedStocks(updatedStocks);
    setChecking(false);
  };

  const parseMarketSurgeCSV = (csvContent: string) => {
    setError(null);
    const lines = csvContent.trim().split('\n');
    if (lines.length < 2) {
      setError('CSV file is empty or invalid');
      return;
    }

    // Parse header to find column indices
    const headerParts = lines[0].match(/("([^"]*)"|[^,]+)/g) || [];
    const headers = headerParts.map(part => part.replace(/^"|"$/g, '').trim());
    
    // Map column names to indices
    const columnIndex: { [key: string]: number } = {};
    headers.forEach((header, index) => {
      columnIndex[header] = index;
    });

    const stocks: ImportedStock[] = [];
    // Skip header row
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Parse CSV line properly handling quotes
      const parts = line.match(/("([^"]*)"|[^,]+)/g) || [];
      const values = parts.map(part => part.replace(/^"|"$/g, '').trim());

      if (values.length >= 8) {
        try {
          // Parse volume-related fields
          const volumeChgVs50Day = parseFloat(values[columnIndex['Vol % Chg vs 50-Day'] || 10]);
          const avgDailyVolume = parseFloat(values[columnIndex['50-Day Avg Vol (1000s)'] || columnIndex['Avg Vol (1000s)'] || 11]) * 1000; // Convert from thousands to actual volume

          // Calculate current volume from avgDailyVolume and volumeChgVs50Day
          // volume = avgDailyVolume * (1 + volumeChgVs50Day/100)
          let volume: number | undefined;
          if (!isNaN(avgDailyVolume) && !isNaN(volumeChgVs50Day)) {
            volume = Math.round(avgDailyVolume * (1 + volumeChgVs50Day / 100));
          }

          const stock: ImportedStock = {
            symbol: values[columnIndex['Symbol'] || 1],
            companyName: values[columnIndex['Name'] || 2],
            currentPrice: parseFloat(values[columnIndex['Current Price'] || 6]),
            weeklyChgToPivot: parseFloat(values[columnIndex['Weekly % Chg to Pivot'] || 3]),
            weeklyBaseType: values[columnIndex['Weekly Base Type'] || 4],
            weeklyBaseStage: values[columnIndex['Weekly Base Stage'] || 5],
            priceChgPercent: parseFloat(values[columnIndex['Price % Chg'] || 8]),
            percentOffHigh: parseFloat(values[columnIndex['% Off High'] || 9]),
            volume,
            volumeChgVs50Day,
            avgDailyVolume,
            marketCap: parseFloat(values[columnIndex['Market Cap (mil)'] || 13]),
            compRating: parseInt(values[columnIndex['Comp Rating'] || 14]),
            epsRating: parseInt(values[columnIndex['EPS Rating'] || 15]),
            rsRating: parseInt(values[columnIndex['RS Rating'] || 16]),
            adRating: values[columnIndex['A/D Rating'] || 17],
            smrRating: values[columnIndex['SMR Rating'] || 18],
            indGroupRank: parseInt(values[columnIndex['Ind Group Rank'] || 19]),
            industryName: values[columnIndex['Industry Name'] || 20],
          };

          // Validate required fields
          if (stock.symbol && !isNaN(stock.currentPrice!)) {
            // Clean up undefined or NaN values
            Object.keys(stock).forEach(key => {
              const value = (stock as any)[key];
              if (value === undefined || (typeof value === 'number' && isNaN(value))) {
                delete (stock as any)[key];
              }
            });
            stocks.push(stock);
          }
          
          console.log('Parsed stock:', stock);
        } catch (e) {
          console.warn(`Failed to parse line ${i}:`, line);
        }
      }
    }

    if (stocks.length === 0) {
      setError('No valid stocks found in CSV');
      return;
    }

    // Extract filename timestamp if available
    if (csvFile && csvFile.name.includes('marketsurge-pivotscreen-')) {
      const match = csvFile.name.match(/marketsurge-pivotscreen-(\d+)\.csv/);
      if (match) {
        const timestamp = parseInt(match[1]);
        console.log('Import timestamp:', timestamp, new Date(timestamp * 1000));
      }
    }

    setParsedStocks(stocks);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      parseMarketSurgeCSV(content);
    };
    reader.readAsText(file);
  };

  const handleManualParse = () => {
    setError(null);
    const lines = importText.trim().split('\n').filter(line => line.trim());
    const stocks: ImportedStock[] = [];

    for (const line of lines) {
      // Support format: SYMBOL or SYMBOL,PIVOT,STOPLOSS,TARGET
      const parts = line.split(/[,\s\t]+/).filter(part => part.trim());
      
      if (parts.length === 1) {
        // Just symbol
        stocks.push({
          symbol: parts[0].toUpperCase(),
        });
      } else if (parts.length === 4) {
        // Full format with levels
        const [symbol, pivot, stop, target] = parts;
        const pivotPrice = parseFloat(pivot);
        const stopLoss = parseFloat(stop);
        const targetPrice = parseFloat(target);

        if (isNaN(pivotPrice) || isNaN(stopLoss) || isNaN(targetPrice)) {
          setError(`Invalid numbers on line: ${line}`);
          return;
        }

        stocks.push({
          symbol: symbol.toUpperCase(),
          pivotPrice,
          stopLoss,
          targetPrice,
        });
      } else {
        setError(`Invalid format on line: ${line}. Use SYMBOL or SYMBOL,PIVOT,STOP,TARGET`);
        return;
      }
    }

    setParsedStocks(stocks);
  };

  const handleRemove = (index: number) => {
    setParsedStocks(parsedStocks.filter((_, i) => i !== index));
  };

  const handleImport = () => {
    onImport(parsedStocks);
    handleClose();
  };

  const handleClose = () => {
    setTabValue(0);
    setImportText('');
    setParsedStocks([]);
    setError(null);
    setCsvFile(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Stocks</DialogTitle>
      <DialogContent>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="MarketSurge CSV" />
          <Tab label="Manual Entry" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Upload your MarketSurge Candidates export (CSV file)
            </Typography>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              component="label"
              sx={{ mt: 1 }}
            >
              Choose CSV File
              <input
                type="file"
                hidden
                accept=".csv"
                onChange={handleFileUpload}
              />
            </Button>
            {csvFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {csvFile.name}
              </Typography>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Enter stocks manually:
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
              • Just symbols: AAPL (one per line)<br />
              • With levels: AAPL,135.50,125.00,155.00<br />
              • Format: Symbol, Pivot, Stop Loss, Target
            </Typography>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            placeholder="AAPL&#10;MSFT&#10;or&#10;AAPL,135.50,125.00,155.00&#10;MSFT,380.00,350.00,420.00"
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Button 
            variant="contained" 
            onClick={handleManualParse} 
            disabled={!importText.trim()}
            sx={{ mb: 2 }}
          >
            Parse Stocks
          </Button>
        </TabPanel>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {parsedStocks.length > 0 && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {parsedStocks.length} stock{parsedStocks.length !== 1 ? 's' : ''} to import
              {parsedStocks.some(s => !s.pivotPrice) && (
                <Box component="span" sx={{ color: 'warning.main', ml: 1 }}>
                  (Pivot prices need to be set manually after import)
                </Box>
              )}
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell align="right">Current Price</TableCell>
                    <TableCell align="center">Base Pattern</TableCell>
                    <TableCell align="center">Weekly % to Pivot</TableCell>
                    <TableCell align="center">Vol % Chg</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedStocks.map((stock, index) => (
                    <TableRow key={index}>
                      <TableCell>{stock.symbol}</TableCell>
                      <TableCell>{stock.companyName || '-'}</TableCell>
                      <TableCell align="right">
                        {stock.currentPrice ? `$${stock.currentPrice.toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {stock.weeklyBaseType ? (
                          <Box>
                            <Typography variant="caption">{stock.weeklyBaseType}</Typography>
                            {stock.weeklyBaseStage && (
                              <Typography variant="caption" display="block" color="textSecondary">
                                Stage {stock.weeklyBaseStage}
                              </Typography>
                            )}
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {stock.weeklyChgToPivot !== undefined ? `${stock.weeklyChgToPivot.toFixed(2)}%` : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {stock.volumeChgVs50Day !== undefined ? (
                          <Chip
                            label={`${stock.volumeChgVs50Day > 0 ? '+' : ''}${stock.volumeChgVs50Day.toFixed(0)}%`}
                            size="small"
                            color={stock.volumeChgVs50Day > 50 ? 'success' : stock.volumeChgVs50Day > 0 ? 'default' : 'error'}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {checking ? (
                          <Chip label="Checking..." size="small" />
                        ) : stock.isUpdate ? (
                          <Chip 
                            label="Update" 
                            size="small" 
                            color="warning" 
                            icon={<UpdateIcon />}
                          />
                        ) : (
                          <Chip 
                            label="New" 
                            size="small" 
                            color="success" 
                            icon={<AddIcon />}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleRemove(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleImport} 
          variant="contained" 
          color="primary"
          disabled={parsedStocks.length === 0}
        >
          Import {parsedStocks.length} Stock{parsedStocks.length !== 1 ? 's' : ''}
          {parsedStocks.filter(s => s.isUpdate).length > 0 && 
            ` (${parsedStocks.filter(s => s.isUpdate).length} updates)`
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportDialog;